import { z } from "zod";
import { CATEGORIES } from "./constants";

// User-friendly error messages
export const ERROR_MESSAGES = {
  FILE_TOO_LARGE: "File size must be less than 10MB. Please compress or choose a smaller file.",
  INVALID_FILE_TYPE: "Please upload a JPG, PNG, or PDF file only.",
  FUTURE_DATE: "Receipt date cannot be in the future. Please check the date.",
  INVALID_AMOUNT: "Please enter a valid amount between $0.01 and $1,000,000.",
  MERCHANT_REQUIRED: "Merchant name is required.",
  CATEGORY_REQUIRED: "Please select a category.",
  RATE_LIMIT: "You've reached the upload limit. Please try again in a few minutes.",
  NETWORK_ERROR: "Connection issue. Please check your internet and try again.",
  DUPLICATE_RECEIPT: "This receipt appears to be a duplicate. Would you like to upload it anyway?",
} as const;

// Receipt upload validation
export const receiptUploadSchema = z.object({
  file: z
    .custom<File>()
    .refine((file) => file instanceof File, "Must be a file")
    .refine((file) => file.size <= 10 * 1024 * 1024, "File must be less than 10MB")
    .refine(
      (file) => ["image/jpeg", "image/jpg", "image/png", "application/pdf"].includes(file.type),
      "Only JPG, PNG, and PDF files are allowed"
    ),
});

// Manual receipt entry validation
export const manualReceiptSchema = z.object({
  merchant_name: z.string()
    .trim()
    .min(1, "Merchant name is required")
    .max(200, "Merchant name must be less than 200 characters"),
  
  category: z.enum(CATEGORIES as unknown as [string, ...string[]], {
    errorMap: () => ({ message: "Please select a valid category" }),
  }),
  
  total: z.number()
    .min(0, "Amount cannot be negative")
    .max(999999.99, "Amount must be less than $1,000,000")
    .refine((val) => Number(val.toFixed(2)) === val, "Amount can only have 2 decimal places"),
  
  date: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format")
    .refine((date) => {
      const d = new Date(date);
      return d instanceof Date && !isNaN(d.getTime());
    }, "Invalid date")
    .refine((date) => {
      const d = new Date(date);
      const now = new Date();
      now.setHours(23, 59, 59, 999); // End of today
      return d <= now;
    }, "Date cannot be in the future"),
  
  subtotal: z.number()
    .nonnegative("Subtotal must be non-negative")
    .max(999999.99, "Subtotal must be less than $1,000,000")
    .optional(),
  
  tax: z.number()
    .nonnegative("Tax must be non-negative")
    .max(99999.99, "Tax must be less than $100,000")
    .optional(),
});

// Chat message validation
export const chatMessageSchema = z.object({
  content: z.string()
    .trim()
    .min(1, "Message cannot be empty")
    .max(2000, "Message must be less than 2000 characters")
    .refine(
      (text) => !/[<>]/.test(text),
      "Message contains invalid HTML characters"
    ),
});

// Profile update validation
export const profileUpdateSchema = z.object({
  full_name: z.string()
    .trim()
    .min(1, "Name is required")
    .max(100, "Name must be less than 100 characters")
    .regex(/^[a-zA-Z\s\-'.]+$/, "Name can only contain letters, spaces, hyphens, and apostrophes"),
  
  phone_number: z.string()
    .trim()
    .max(20, "Phone number must be less than 20 characters")
    .regex(/^[+\d\s\-()]+$/, "Phone number contains invalid characters")
    .optional()
    .or(z.literal("")),
});

// Email validation helper
export const emailSchema = z.string()
  .trim()
  .email("Invalid email address")
  .max(255, "Email must be less than 255 characters")
  .toLowerCase();

// Password validation for authentication
export const passwordSchema = z.string()
  .min(8, "Password must be at least 8 characters")
  .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
  .regex(/[a-z]/, "Password must contain at least one lowercase letter")
  .regex(/[0-9]/, "Password must contain at least one number")
  .regex(/[^A-Za-z0-9]/, "Password must contain at least one special character");

// Authentication form validation
export const authSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
});

// Onboarding form validation
export const onboardingSchema = z.object({
  use_case: z.enum(['personal', 'business'], {
    errorMap: () => ({ message: "Please select a valid use case" }),
  }),
  business_type: z.string()
    .trim()
    .min(2, "Business type must be at least 2 characters")
    .max(100, "Business type must be less than 100 characters")
    .regex(/^[a-zA-Z0-9\s\-\/&,.]+$/, "Business type contains invalid characters")
    .optional()
    .or(z.literal("")),
  estimated_income: z.string()
    .regex(/^\d*$/, "Income must contain only numbers")
    .transform(val => val ? parseInt(val, 10) : null)
    .refine(val => val === null || (val >= 0 && val <= 100000000), {
      message: "Income must be between 0 and 100,000,000"
    })
    .optional(),
});

// Sanitize HTML to prevent XSS
export function sanitizeHTML(text: string): string {
  return text
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;")
    .replace(/\//g, "&#x2F;");
}

// Validate file type by content (magic numbers)
export async function validateFileType(file: File): Promise<boolean> {
  const buffer = await file.slice(0, 4).arrayBuffer();
  const bytes = new Uint8Array(buffer);
  
  // JPEG: FF D8 FF
  if (bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) {
    return true;
  }
  
  // PNG: 89 50 4E 47
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) {
    return true;
  }
  
  // WebP: 52 49 46 46 (RIFF)
  if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46) {
    return true;
  }
  
  // PDF: 25 50 44 46 (%PDF)
  if (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
    return true;
  }
  
  return false;
}

// Rate limit checker for client-side
export function checkClientRateLimit(action: string, maxActions: number, windowMs: number): boolean {
  const key = `rate_limit_${action}`;
  const now = Date.now();
  const windowStart = now - windowMs;
  
  // Get stored actions
  const stored = localStorage.getItem(key);
  let actions: number[] = stored ? JSON.parse(stored) : [];
  
  // Remove expired actions
  actions = actions.filter(timestamp => timestamp > windowStart);
  
  // Check if limit exceeded
  if (actions.length >= maxActions) {
    return false;
  }
  
  // Add current action
  actions.push(now);
  localStorage.setItem(key, JSON.stringify(actions));
  
  return true;
}
